import { authClient } from "../../../lib/auth";
import { apiBase, apiHeaders } from "../../../lib/api-base";
import { toAbsoluteRedirectUrl } from "../utils/redirect";

/**
 * Social sign-in is on unless a build explicitly turns it off.
 *
 * These were opt-in (`=== "true"`), and nothing ever opted in: `.env.production`
 * is gitignored, so a build-time flag cannot be committed, and the Cloudflare
 * build has no other source for it. The result was that Google and Apple were
 * fully built, wired and translated — and invisible on majourneys.com, with
 * the sign-in modal offering email only.
 *
 * Both providers were verified against the live API before this flipped:
 * `/api/auth/sign-in/social` returns an authorize URL for each, Google reaches
 * its real consent flow with `api.majourneys.com/api/auth/callback/google`
 * accepted (no redirect_uri_mismatch), and Apple renders its sign-in page for
 * `client_id=com.majourneys.web`.
 *
 * Same shape as `apiBase()`: a correct production default in code, with the
 * env var kept as a kill switch. Set either to "false" to hide a provider.
 */
export const googleAuthEnabled = import.meta.env.VITE_GOOGLE_AUTH_ENABLED !== "false";
export const appleAuthEnabled = import.meta.env.VITE_APPLE_AUTH_ENABLED !== "false";

async function request<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    credentials: "include",
    headers: apiHeaders({
      "content-type": "application/json",
      ...(init.headers ?? {}),
    }),
  });

  const body = (await response.json().catch(() => null)) as
    | { message?: string; error?: { message?: string } }
    | null;

  if (!response.ok) {
    throw new Error(body?.message ?? body?.error?.message ?? "Request failed.");
  }

  return body as T;
}

export const authApi = {
  // Passwordless: `type: "sign-in"` covers sign-up too — better-auth creates
  // the account on verify when the email is new (same flow as mobile).
  sendSignInCode: (input: { email: string }) =>
    authClient.emailOtp.sendVerificationOtp({
      email: input.email.trim().toLowerCase(),
      type: "sign-in",
    }),
  verifySignInCode: (input: { email: string; code: string }) =>
    authClient.signIn.emailOtp({
      email: input.email.trim().toLowerCase(),
      otp: input.code,
    }),
  signOut: () => authClient.signOut(),
  resendVerificationEmail: (input: { email: string }) =>
    authClient.sendVerificationEmail({
      email: input.email,
      callbackURL: toAbsoluteRedirectUrl("/account"),
    }),
  signInWithGoogle: (input: { callbackPath: string }) =>
    authClient.signIn.social({
      provider: "google",
      callbackURL: toAbsoluteRedirectUrl(input.callbackPath),
    }),
  signUpWithGoogle: (input: { callbackPath: string }) =>
    authClient.signIn.social({
      provider: "google",
      callbackURL: toAbsoluteRedirectUrl(input.callbackPath),
      requestSignUp: true,
    }),
  signInWithApple: (input: { callbackPath: string }) =>
    authClient.signIn.social({
      provider: "apple",
      callbackURL: toAbsoluteRedirectUrl(input.callbackPath),
    }),
  signUpWithApple: (input: { callbackPath: string }) =>
    authClient.signIn.social({
      provider: "apple",
      callbackURL: toAbsoluteRedirectUrl(input.callbackPath),
      requestSignUp: true,
    }),
  // Phone verification removed 2026-07-14 — phones are plain optional input.
  completeSignUp: (input: {
    firstName: string;
    lastName: string;
    currency: string;
    language: string;
    phoneCode?: string;
    phoneNumber?: string;
  }) =>
    request<{ ok: true }>("/me/complete-sign-up", {
      method: "POST",
      body: JSON.stringify(input),
    }),
};
